<template>
  <v-dialog v-model="open" width="800" persistent scrollable>
    <v-card class="dlg-card timeline-card">
      <dialog-header
        title="Timeline"
        subtitle="Track the progress and key events"
        icon="mdi-timeline-clock-outline"
        @close="$emit('close-dialog')"
      />
      <v-divider></v-divider>
      <v-card-text
        ref="scrollArea"
        class="px-2 tw-bg-gray-100 dlg-body timeline-scroll"
        @scroll="onScroll"
      >
        <div class="tw-px-6 tw-pb-6 tw-pt-3">
          <div
            v-if="isLoading"
            class="tw-flex tw-justify-center tw-items-center tw-py-8"
          >
            <v-progress-circular indeterminate></v-progress-circular>
          </div>

          <div v-else-if="!isLoading && tatReport && timeline">
            <v-timeline align-top class="tl">
              <v-timeline-item
                v-for="(process, index) in timeline"
                :key="index"
                :icon="process.icon"
                color="primary"
                fill-dot
                large
              >
                <template v-slot:opposite>
                  <div class="tl-date">
                    <div class="tl-date-icon">
                      <v-icon size="16" color="primary"
                        >mdi-calendar-blank-outline</v-icon
                      >
                    </div>
                    <div class="tl-date-text">
                      <div class="tl-date-main">
                        {{ process.started | formatToHumanWithTime }}
                      </div>
                      <div class="tl-date-sub">
                        to {{ process.ended | formatToHumanWithTime }}
                      </div>
                    </div>
                  </div>
                </template>
                <div class="tl-card">
                  <div class="tl-stage">{{ formatTitle(process.name) }}</div>
                  <div v-if="process.name == 'BOOKING'" class="tl-booking">
                    <span class="tl-booking-id">{{ tatReport.bookingId }}</span>
                    <div class="tl-line">
                      <v-icon size="15" color="primary"
                        >mdi-file-document-outline</v-icon
                      >
                      <span class="tl-line-label">Reg No.:</span>
                      <span class="tl-line-value">{{
                        tatReport.bookingData.vehicle.registrationNumber
                      }}</span>
                    </div>
                  </div>
                  <div class="tl-line">
                    <v-icon size="15" color="primary">mdi-clock-outline</v-icon>
                    <span class="tl-line-label">Duration:</span>
                    <span class="tl-line-value">{{ process.timeTaken }}</span>
                  </div>
                  <span class="tl-status" :class="statusClass(process.status)">
                    <span class="tl-status-dot"></span>{{ process.status }}
                  </span>
                </div>
              </v-timeline-item>
            </v-timeline>

            <div class="tl-actors">
              <div
                class="tl-actor"
                v-for="(actor, index) in actors"
                :key="index"
              >
                <div class="tl-actor-icon">
                  <v-icon size="18" color="primary">mdi-account-outline</v-icon>
                </div>
                <div class="tl-actor-text">
                  <div class="tl-actor-title">{{ actor.title }}</div>
                  <div
                    v-if="!actor.email && !actor.fullName && !actor.phoneNumber"
                    class="tl-actor-sub"
                  >
                    N/A
                  </div>
                  <div v-else class="tl-actor-sub">
                    <span v-if="actor.email">{{ actor.email }}</span>
                    <span v-if="actor.fullName" class="tl-actor-name">{{
                      actor.fullName
                    }}</span>
                    <span v-if="actor.phoneNumber">{{
                      actor.phoneNumber
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-else
            class="tw-flex tw-flex-col tw-justify-center tw-items-center tw-py-8"
          >
            <p class="c-gray-text">Failed to get TAT report</p>

            <v-btn color="secondary" @click="fetchTatReport">Retry</v-btn>
          </div>
        </div>
      </v-card-text>
      <v-btn
        v-show="showScrollDown"
        fab
        small
        color="primary"
        class="timeline-scroll-fab"
        aria-label="Scroll down"
        @click="scrollDown"
      >
        <v-icon>mdi-chevron-down</v-icon>
      </v-btn>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { formatToHumanWithTime } from '@/utils/time'
import DialogHeader from '@/components/DialogHeader'

export default {
  components: { DialogHeader },
  emits: ['close-dialog'],
  props: {
    id: {
      type: String,
      required: true,
    },
    open: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    isLoading: false,
    tatReport: null,
    atBottom: false,
    isScrollable: false,
  }),
  computed: {
    timeline() {
      if (!this.isLoading && this.tatReport) {
        return [
          {
            icon: 'mdi-car',
            ...this.tatReport.booking,
          },
          {
            icon: 'mdi-car-search',
            ...this.tatReport.inspection,
          },
          {
            icon: 'mdi-file-document',
            ...this.tatReport.bidTemplatePreparation,
          },
          {
            icon: 'mdi-text-box-plus',
            ...this.tatReport.bidding,
          },
          {
            icon: 'mdi-text-box-search',
            ...this.tatReport.bidAnalysis,
          },
          {
            icon: 'mdi-text-box-check',
            ...this.tatReport.bidApproval,
          },
          {
            icon: 'mdi-car-back',
            ...this.tatReport.assessmentCheckout,
          },
          { icon: 'mdi-car', ...this.tatReport.garageCheckin },
          { icon: 'mdi-timer-sand', ...this.tatReport.awaitingRepairs },
          { icon: 'mdi-car-wrench', ...this.tatReport.repairsInProgress },
          {
            icon: 'mdi-file-document',
            ...this.tatReport.awaitingSupplementaryApproval,
          },
          { icon: 'mdi-file-check', ...this.tatReport.supplementaryApproval },
          { icon: 'mdi-magnify', ...this.tatReport.reInspection },
          { icon: 'mdi-car-back', ...this.tatReport.release },
        ].filter((item) => item.status !== 'N/A')
      }
      return null
    },
    actors() {
      if (!this.isLoading && this.tatReport) {
        return [
          {
            title: 'Internal Assessor',
            email: this.tatReport.bookingData.internalAssessor.email,
            fullName: this.tatReport.bookingData.internalAssessor.fullName,
          },
          {
            title: 'External Assessor',
            email: this.tatReport.bookingData.externalAssessor.email,
            fullName: this.tatReport.bookingData.externalAssessor.fullName,
          },
          {
            title: 'Approver',
            email: this.tatReport.bookingData.approver.email,
            fullName: this.tatReport.bookingData.approver.fullName,
          },
          {
            title: 'Analyst',
            email: this.tatReport.bookingData.analyst.email,
            fullName: this.tatReport.bookingData.analyst.fullName,
          },
          {
            title: 'Claim Officer',
            email: this.tatReport.bookingData.claimOfficer.email,
            fullName: this.tatReport.bookingData.claimOfficer.fullName,
          },
          {
            title: 'Garage',
            email: this.tatReport.bookingData.garage.email,
            fullName: this.tatReport.bookingData.garage.name,
            phoneNumber: this.tatReport.bookingData.garage.phoneNumber,
          },
        ]
      }

      return []
    },
    showScrollDown() {
      return this.isScrollable && !this.atBottom
    },
  },
  methods: {
    async fetchTatReport() {
      this.isLoading = true

      axios
        .get(`/motor-assessment/api/reports/tat/timeline/${this.id}`)
        .then(({ data }) => {
          if (data.success) {
            this.tatReport = data.data
          } else {
            this.$store.commit('SNACKBAR', {
              state: 'failed',
              message: data.msg ?? 'Failed to fetch timeline',
              variant: 'error',
            })
          }
        })
        .catch((err) => {
          this.$store.commit('SNACKBAR', {
            state: 'failed',
            message: err.message ?? 'Failed to fetch timeline',
            variant: 'error',
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    formatTitle(input) {
      return input.replace(/_/g, ' ')
    },
    statusClass(status) {
      if (status === 'OnTime') return 'is-ontime'
      if (status === 'InProgress') return 'is-progress'
      if (status === 'Missing TAT Config' || status === 'N/A') return 'is-muted'
      return 'is-error'
    },
    scrollEl() {
      // v-card-text is a Vuetify component; its scrollable DOM node is $el.
      const ref = this.$refs.scrollArea
      return ref && ref.$el ? ref.$el : ref
    },
    scrollDown() {
      const el = this.scrollEl()
      if (!el) return
      el.scrollBy({
        top: Math.round(el.clientHeight * 0.8),
        behavior: 'smooth',
      })
    },
    onScroll() {
      const el = this.scrollEl()
      if (!el) return
      // Within 24px of the bottom counts as "at bottom".
      this.atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24
      this.isScrollable = el.scrollHeight > el.clientHeight + 4
    },
    updateScrollState() {
      this.$nextTick(() => this.onScroll())
    },
  },
  watch: {
    // Recalculate once the timeline content has rendered.
    tatReport() {
      this.updateScrollState()
    },
  },
  mounted() {
    this.fetchTatReport()
  },
  filters: {
    formatToHumanWithTime,
  },
}
</script>

<style scoped>
/* The card is the positioning context for the floating scroll-down button. */
.timeline-card {
  position: relative;
}
/* Hide the scrollbar visually — wheel/touch scrolling still works. */
.timeline-scroll {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}
.timeline-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
/* Floating "scroll down" affordance, centered at the bottom of the dialog. */
.timeline-scroll-fab {
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 2;
  box-shadow: 0 6px 16px rgba(3, 78, 162, 0.35);
}

/* ── Timeline styling ─────────────────────────────────────────────── */
.tl ::v-deep .v-timeline-item__dot {
  box-shadow: 0 4px 12px rgba(3, 78, 162, 0.3);
}
.tl ::v-deep .v-timeline::before {
  background: #d6e2f4;
}

/* Stage card */
.tl-card {
  background: #ffffff;
  border: 1px solid #e8edf5;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(15, 42, 85, 0.06);
  padding: 14px 16px;
}
.tl-stage {
  font-size: 15px;
  font-weight: 800;
  color: #0f2a55;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
}
.tl-booking {
  margin-bottom: 6px;
}
.tl-booking-id {
  color: #034ea2;
  font-weight: 700;
  font-size: 14px;
}
.tl-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.tl-line-label {
  color: #6b7a90;
  font-size: 13px;
}
.tl-line-value {
  color: #0f2a55;
  font-size: 13px;
  font-weight: 600;
}
/* Status pill */
.tl-status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 12px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}
.tl-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.tl-status.is-ontime {
  background: #e7f6ec;
  color: #1b8a4b;
}
.tl-status.is-error {
  background: #fdecec;
  color: #d64545;
}
.tl-status.is-progress {
  background: #fdf1e3;
  color: #d98324;
}
.tl-status.is-muted {
  background: #eef1f5;
  color: #6b7a90;
}
/* Date block (opposite side) */
.tl-date {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.tl-date-icon {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: #eef4fd;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tl-date-main {
  color: #0f2a55;
  font-weight: 700;
  font-size: 13px;
}
.tl-date-sub {
  color: #6b7a90;
  font-size: 12px;
}
/* Actors footer card */
.tl-actors {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 20px;
  background: #f6f9fe;
  border: 1px solid #e8edf5;
  border-radius: 14px;
  padding: 16px 18px;
}
.tl-actor {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}
.tl-actor-icon {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: #eaf1fb;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tl-actor-title {
  color: #0f2a55;
  font-weight: 700;
  font-size: 13px;
}
.tl-actor-sub {
  color: #5b6b82;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  overflow-wrap: break-word;
}
.tl-actor-name {
  color: #034ea2;
}
</style>
